import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, Loader2Icon, UserIcon, BotIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { CopyButton } from './CopyButton';
import { marked } from 'marked';
type Message = {
  role: 'user' | 'assistant' | 'system';
  content: string;
};
export function GrokInterface({
  initialPrompt = ''
}) {
  const [messages, setMessages] = useState<Message[]>([{
    role: 'system',
    content: `You are Grok, a highly intelligent, helpful AI assistant designed to assist teachers.
Please format your responses using markdown to make them visually appealing and well-structured:
1. Use **bold text** for important points, headings, and key concepts
2. Use proper headings (# Main Heading, ## Subheading) to organize information
3. Use numbered lists (1. 2. 3.) for sequential steps or instructions
4. Use bullet points (- or *) for related items or options
5. Use > blockquotes for examples or to highlight important information
6. Use horizontal rules (---) to separate major sections
When asked to provide content in multiple languages:
- Create separate, clearly labeled sections for each language
- Use a heading like "# [Language Name]" to start each language section
- Provide complete responses in each language (not just translations of headings)
- Ensure each language section contains the full content requested
Your responses should be well-structured, easy to read, and ready for teachers to print or copy into their documents. Use appropriate spacing between sections to improve readability.`
  }]);
  const [visibleMessages, setVisibleMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const apiKey = 'xai-okF3ioGOnMqxv9qIxKkahzQc0dqYjY67cDxRGIRzHl1mByNl5TQGWAektOBriSiuEXQbNqIHUgvdnzjv';
  // Initialize visible messages (filtering out system messages)
  useEffect(() => {
    setVisibleMessages(messages.filter(msg => msg.role !== 'system'));
  }, [messages]);
  // Handle initialPrompt changes
  useEffect(() => {
    if (initialPrompt && initialPrompt.trim() !== '') {
      setInput(initialPrompt);
      // Automatically submit the prompt if it's coming from the Send to Grok button
      const userMessage: Message = {
        role: 'user',
        content: initialPrompt
      };
      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);
      // Call the API with the prompt
      fetch('https://api.x.ai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          model: 'grok-4-0709',
          temperature: 0.7,
          max_tokens: 4000,
          stream: false
        })
      }).then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`API request failed with status ${response.status}: ${text}`);
          });
        }
        return response.json();
      }).then(data => {
        if (data.choices && data.choices.length > 0 && data.choices[0].message) {
          const assistantMessage: Message = {
            role: 'assistant',
            content: data.choices[0].message.content
          };
          setMessages(prev => [...prev, assistantMessage]);
        } else {
          throw new Error('Incomplete or invalid response from API');
        }
      }).catch(error => {
        console.error('Error calling Grok API:', error);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Sorry, I encountered an error. Please try again later. Error details: ${error.message}`
        }]);
      }).finally(() => {
        setIsLoading(false);
        setInput('');
      });
    }
  }, [initialPrompt]);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [visibleMessages]);
  // Convert markdown to HTML for rich text copying
  const markdownToHtml = markdown => {
    try {
      return marked(markdown, {
        mangle: false,
        headerIds: false,
        gfm: true
      });
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);
      return '';
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const userMessage: Message = {
      role: 'user',
      content: input
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch('https://api.x.ai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          model: 'grok-4-0709',
          temperature: 0.7,
          max_tokens: 4000,
          stream: false
        })
      });
      if (!response.ok) {
        const errorData = await response.text();
        console.error('API Error:', errorData);
        throw new Error(`API request failed with status ${response.status}: ${errorData}`);
      }
      const data = await response.json();
      console.log('API Response:', data); // Log the full response for debugging
      // Check if we have a complete response
      if (data.choices && data.choices.length > 0 && data.choices[0].message) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.choices[0].message.content
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('Incomplete or invalid response from API');
      }
    } catch (error) {
      console.error('Error calling Grok API:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I encountered an error. Please try again later. Error details: ${error.message}`
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12" data-id="element-9">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-indigo-100" data-id="element-10">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-500 px-4 sm:px-6 py-3 sm:py-4 flex items-center" data-id="element-11">
          <div className="bg-white/20 rounded-full p-2 mr-3" data-id="element-12">
            <BotIcon className="text-white w-5 h-5 sm:w-6 sm:h-6" data-id="element-13" />
          </div>
          <div data-id="element-14">
            <h2 className="text-lg sm:text-xl font-bold text-white" data-id="element-15">Grok 4</h2>
          </div>
        </div>
        <div className="h-[400px] sm:h-[500px] overflow-y-auto p-3 sm:p-6 bg-gray-50" data-id="element-16">
          {visibleMessages.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-center text-gray-500" data-id="element-17">
              <BotIcon size={36} className="mb-3 sm:mb-4 text-indigo-300 sm:w-12 sm:h-12" data-id="element-18" />
              <h3 className="text-lg sm:text-xl font-medium mb-2" data-id="element-19">
                Chat with Grok 4
              </h3>
              <p className="max-w-md text-sm sm:text-base" data-id="element-20">
                Try asking Grok about anything or use your generated teaching
                prompt from above.
              </p>
            </div> : <div className="space-y-4" data-id="element-21">
              {visibleMessages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-22">
                  <div className={`max-w-[90%] sm:max-w-[80%] px-3 py-2 sm:px-4 sm:py-3 rounded-lg ${message.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-none' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'}`} data-id="element-23">
                    <div className="flex items-center mb-1 justify-between" data-id="element-24">
                      <div className="flex items-center" data-id="element-25">
                        {message.role === 'assistant' ? <BotIcon size={14} className="mr-1.5 sm:mr-2 text-indigo-600 sm:w-4 sm:h-4" data-id="element-26" /> : <UserIcon size={14} className="mr-1.5 sm:mr-2 text-white sm:w-4 sm:h-4" data-id="element-27" />}
                        <span className="text-xs font-medium" data-id="element-28">
                          {message.role === 'user' ? 'You' : 'Grok'}
                        </span>
                      </div>
                      {message.role === 'assistant' && <CopyButton textToCopy={message.content} htmlContent={markdownToHtml(message.content)} data-id="element-29" />}
                    </div>
                    <div className="whitespace-pre-wrap markdown-content overflow-auto text-sm sm:text-base" data-id="element-30">
                      {message.role === 'assistant' ? <ReactMarkdown components={{
                  h1: ({
                    node,
                    ...props
                  }) => <h1 className="text-lg sm:text-xl font-bold mt-3 mb-2 text-indigo-800" {...props} data-id="element-32" />,
                  h2: ({
                    node,
                    ...props
                  }) => <h2 className="text-base sm:text-lg font-bold mt-3 mb-2 text-indigo-700" {...props} data-id="element-33" />,
                  h3: ({
                    node,
                    ...props
                  }) => <h3 className="text-sm sm:text-md font-bold mt-2 mb-1 text-indigo-600" {...props} data-id="element-34" />,
                  p: ({
                    node,
                    ...props
                  }) => <p className="mb-2 text-sm sm:text-base" {...props} data-id="element-35" />,
                  ul: ({
                    node,
                    ...props
                  }) => <ul className="list-disc pl-4 sm:pl-5 mb-2 space-y-1" {...props} data-id="element-36" />,
                  ol: ({
                    node,
                    ...props
                  }) => <ol className="list-decimal pl-4 sm:pl-5 mb-2 space-y-1" {...props} data-id="element-37" />,
                  li: ({
                    node,
                    ...props
                  }) => <li className="mb-1 text-sm sm:text-base" {...props} data-id="element-38" />,
                  blockquote: ({
                    node,
                    ...props
                  }) => <blockquote className="border-l-4 border-indigo-300 pl-2 sm:pl-3 italic my-2 text-gray-600 text-sm sm:text-base" {...props} data-id="element-39" />,
                  hr: ({
                    node,
                    ...props
                  }) => <hr className="my-3 border-t border-gray-300" {...props} data-id="element-40" />,
                  strong: ({
                    node,
                    ...props
                  }) => <strong className="font-bold text-indigo-900" {...props} data-id="element-41" />,
                  pre: ({
                    node,
                    ...props
                  }) => <pre className="bg-gray-100 p-2 rounded my-2 overflow-x-auto text-xs sm:text-sm" {...props} data-id="element-42" />,
                  code: ({
                    node,
                    inline,
                    ...props
                  }) => inline ? <code className="bg-gray-100 px-1 py-0.5 rounded text-pink-600 text-xs sm:text-sm" {...props} data-id="element-43" /> : <code className="block overflow-x-auto text-xs sm:text-sm" {...props} data-id="element-44" />,
                  table: ({
                    node,
                    ...props
                  }) => <div className="overflow-x-auto my-2" data-id="element-45">
                                <table className="min-w-full border border-gray-300 text-sm" {...props} data-id="element-46" />
                              </div>,
                  thead: ({
                    node,
                    ...props
                  }) => <thead className="bg-gray-100" {...props} data-id="element-47" />,
                  tbody: ({
                    node,
                    ...props
                  }) => <tbody className="divide-y divide-gray-300" {...props} data-id="element-48" />,
                  tr: ({
                    node,
                    ...props
                  }) => <tr className="hover:bg-gray-50" {...props} data-id="element-49" />,
                  th: ({
                    node,
                    ...props
                  }) => <th className="px-3 py-2 border-b border-gray-300 text-left font-medium text-xs sm:text-sm" {...props} data-id="element-50" />,
                  td: ({
                    node,
                    ...props
                  }) => <td className="px-3 py-2 border-b border-gray-300 text-xs sm:text-sm" {...props} data-id="element-51" />
                }} data-id="element-31">
                          {message.content}
                        </ReactMarkdown> : message.content}
                    </div>
                  </div>
                </div>)}
              {isLoading && <div className="flex justify-start" data-id="element-52">
                  <div className="bg-white border border-gray-200 text-gray-800 rounded-lg rounded-tl-none max-w-[90%] sm:max-w-[80%] px-3 py-2 sm:px-4 sm:py-3" data-id="element-53">
                    <div className="flex items-center mb-1" data-id="element-54">
                      <BotIcon size={14} className="mr-1.5 sm:mr-2 text-indigo-600 sm:w-4 sm:h-4" data-id="element-55" />
                      <span className="text-xs font-medium" data-id="element-56">Grok</span>
                    </div>
                    <Loader2Icon className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" data-id="element-57" />
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-58" />
            </div>}
        </div>
        <div className="border-t border-gray-200 p-3 sm:p-4 bg-white" data-id="element-59">
          <form onSubmit={handleSubmit} className="flex items-center" data-id="element-60">
            <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Type your message..." className="flex-grow px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" disabled={isLoading} data-id="element-61" />
            <button type="submit" className="bg-indigo-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-r-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-indigo-300 min-w-[40px] sm:min-w-[48px] flex items-center justify-center" disabled={isLoading || !input.trim()} data-id="element-62">
              {isLoading ? <Loader2Icon className="animate-spin h-4 w-4 sm:h-5 sm:w-5" data-id="element-63" /> : <SendIcon size={16} className="sm:w-[18px] sm:h-[18px]" data-id="element-64" />}
            </button>
          </form>
          <div className="mt-2 text-xs text-gray-500 text-center space-y-1" data-id="element-65">
            <p data-id="element-66">
              Your messages will be processed by xAI's Grok 4 model. Be mindful
              of sensitive information.
            </p>
            <p className="text-indigo-500" data-id="element-67">
              Responses are formatted with markdown for easy reading and
              printing.
            </p>
          </div>
        </div>
      </div>
    </div>;
}